import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getZonesRequest,
  createZoneRequest,
  createZoneMassiveRequest,
  updateZoneRequest,
  updateZoneMassiveRequest,
  deleteZoneRequest,
  getZoneDetailRequest,
  getZoneCommunesRequest,
  getZoneAssignmentsRequest,
  createAssignmentRequest,
  updateAssignmentRequest,
  deleteAssignmentRequest,
  getCourierOptionsRequest,
  getAdminOptionsRequest,
  getWarehouseOptionsRequest,
  getAdminShippingZonesRequest,
} from './zonesApi';
import { isMassiveCountry } from './zonesUtils';
import { selectIsStaff } from '../auth/authSelectors';
import { uploadProgressReporter } from '../feedback/uploadProgress';

const SENDING_SHEET = 'Subiendo la planilla';

/**
 * @typedef {object} ZoneFormValues
 * @property {string} name
 * @property {string} courier
 * @property {number|string} admin
 * @property {number|string} price
 * @property {string} country - ISO2 code driving the save strategy
 * @property {Array<number>} communes - Only meaningful for Chile
 * @property {File} [file] - Only meaningful for Mexico
 */

/**
 * Maps form values onto the JSON payload.
 *
 * `commune_ids` is deliberately omitted for massive countries: their coverage
 * lives in the uploaded spreadsheet, and sending an empty array on a PATCH
 * would wipe it.
 *
 * @param {ZoneFormValues} zoneData
 * @returns {object}
 */
const buildZonePayload = (zoneData) => {
  const payload = {
    name: zoneData.name,
    courier: zoneData.courier,
    admin: zoneData.admin,
    price: zoneData.price,
    country: zoneData.country,
  };

  if (!isMassiveCountry(zoneData.country)) {
    payload.commune_ids = zoneData.communes || [];
  }

  return payload;
};

/* -------------------------------------------------------------------------- */
/*                                    List                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fetches a page of shipping zones using the filters currently held in the
 * store, mirroring how sellers and couriers read their own state.
 */
export const fetchZones = createAsyncThunk(
  'zones/fetchZones',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { search, page, rowsPerPage } = getState().zones.filters;

      const params = {
        page,
        page_size: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      const response = await getZonesRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching shipping zones:', error);
      return rejectWithValue('No se pudieron obtener las zonas de despacho');
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                 Zone CRUD                                  */
/* -------------------------------------------------------------------------- */

/**
 * Creates a zone, routing to the JSON or the multipart endpoint depending on
 * the country.
 *
 * Takes a {@link ZoneFormValues} object as its argument.
 */
export const createZone = createAsyncThunk(
  'zones/createZone',
  async (zoneData, { dispatch, requestId, rejectWithValue }) => {
    try {
      const response = isMassiveCountry(zoneData.country)
        ? await createZoneMassiveRequest(
          zoneData,
          uploadProgressReporter(dispatch, requestId, SENDING_SHEET)()
        )
        : await createZoneRequest(buildZonePayload(zoneData));

      return response.data;
    } catch (error) {
      console.error('Error creating shipping zone:', error);
      return rejectWithValue('No se pudo crear la zona de despacho');
    }
  }
);

/**
 * Updates a zone. A massive zone only goes through the multipart endpoint when
 * a new spreadsheet is attached; otherwise its existing coverage is preserved.
 *
 * Takes `{ zoneId, zoneData }`, where `zoneData` is a {@link ZoneFormValues}.
 */
export const updateZone = createAsyncThunk(
  'zones/updateZone',
  async ({ zoneId, zoneData }, { dispatch, requestId, rejectWithValue }) => {
    try {
      const shouldReplaceCoverage = isMassiveCountry(zoneData.country) && Boolean(zoneData.file);

      const response = shouldReplaceCoverage
        ? await updateZoneMassiveRequest(
          zoneId,
          zoneData,
          uploadProgressReporter(dispatch, requestId, SENDING_SHEET)()
        )
        : await updateZoneRequest(zoneId, buildZonePayload(zoneData));

      return response.data;
    } catch (error) {
      console.error('Error updating shipping zone:', error);
      return rejectWithValue('No se pudo actualizar la zona de despacho');
    }
  }
);

/** Deletes a zone and returns its id so the caller can refresh its list. */
export const deleteZone = createAsyncThunk(
  'zones/deleteZone',
  async (zoneId, { rejectWithValue }) => {
    try {
      await deleteZoneRequest(zoneId);
      return zoneId;
    } catch (error) {
      console.error('Error deleting shipping zone:', error);
      return rejectWithValue('No se pudo eliminar la zona de despacho');
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                   Detail                                   */
/* -------------------------------------------------------------------------- */

/**
 * Loads the zone header data. Kept separate from the coverage and the
 * assignments so paging through communes never re-renders the whole view.
 *
 * @param {number|string} zoneId
 */
export const fetchZoneDetail = createAsyncThunk(
  'zones/fetchZoneDetail',
  async (zoneId, { rejectWithValue }) => {
    if (!zoneId) return rejectWithValue('Zone ID no provisto');

    try {
      const response = await getZoneDetailRequest(zoneId);
      return response.data;
    } catch (error) {
      console.error('Error fetching shipping zone detail:', error);
      return rejectWithValue('Error al cargar la zona de despacho');
    }
  }
);

/**
 * Loads the communes covered by the zone, using the pagination and search held
 * in `zones.communesFilters`.
 *
 * @param {number|string} zoneId
 */
export const fetchZoneCommunes = createAsyncThunk(
  'zones/fetchZoneCommunes',
  async (zoneId, { getState, rejectWithValue }) => {
    if (!zoneId) return rejectWithValue('Zone ID no provisto');

    try {
      const { page, rowsPerPage, search } = getState().zones.communesFilters;

      const params = {
        page,
        page_size: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      const response = await getZoneCommunesRequest(zoneId, params);
      return response.data;
    } catch (error) {
      console.error('Error fetching zone communes:', error);
      return rejectWithValue('No se pudieron cargar las comunas de esta zona');
    }
  }
);

/** @param {number|string} zoneId */
export const fetchZoneAssignments = createAsyncThunk(
  'zones/fetchZoneAssignments',
  async (zoneId, { rejectWithValue }) => {
    if (!zoneId) return rejectWithValue('Zone ID no provisto');

    try {
      const response = await getZoneAssignmentsRequest(zoneId);
      return response.data;
    } catch (error) {
      console.error('Error fetching zone assignments:', error);
      return rejectWithValue('No se pudieron cargar las bodegas asignadas');
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                        Warehouse ↔ zone assignments                        */
/* -------------------------------------------------------------------------- */

/**
 * Assigns a warehouse to the zone.
 *
 * Takes `{ zoneId, assignmentData }`, where `assignmentData` holds
 * `{ warehouse, shipping_method }`.
 */
export const createAssignment = createAsyncThunk(
  'zones/createAssignment',
  async ({ zoneId, assignmentData }, { rejectWithValue }) => {
    try {
      const payload = { ...assignmentData, shipping_zone: parseInt(zoneId, 10) };
      const response = await createAssignmentRequest(payload);
      return response.data;
    } catch (error) {
      console.error('Error creating warehouse assignment:', error);
      return rejectWithValue('No se pudo asignar la bodega a esta zona');
    }
  }
);

/** Takes `{ assignmentId, assignmentData }`. */
export const updateAssignment = createAsyncThunk(
  'zones/updateAssignment',
  async ({ assignmentId, assignmentData }, { rejectWithValue }) => {
    try {
      const response = await updateAssignmentRequest(assignmentId, assignmentData);
      return response.data;
    } catch (error) {
      console.error('Error updating warehouse assignment:', error);
      return rejectWithValue('No se pudo actualizar la asignación');
    }
  }
);

/** @param {number|string} assignmentId */
export const deleteAssignment = createAsyncThunk(
  'zones/deleteAssignment',
  async (assignmentId, { rejectWithValue }) => {
    try {
      await deleteAssignmentRequest(assignmentId);
      return assignmentId;
    } catch (error) {
      console.error('Error deleting warehouse assignment:', error);
      return rejectWithValue('No se pudo eliminar la asignación');
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                              Form select options                           */
/* -------------------------------------------------------------------------- */

/**
 * Loads the options shared by the zone create/edit form.
 *
 * Skips the request when they are already in the store: these catalogs barely
 * change and the form is opened repeatedly across the list and detail views.
 */
export const fetchZoneFormOptions = createAsyncThunk(
  'zones/fetchZoneFormOptions',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Only staff can hand a zone to another admin, so only staff asks for the
      // roster. `admins/list/` carries no permission class and answers with
      // every admin's email, name, rut and plan to anyone signed in; this at
      // least stops the new front from being one of its callers.
      const isStaff = selectIsStaff(getState());

      const [courierResponse, adminResponse] = await Promise.all([
        getCourierOptionsRequest(),
        isStaff ? getAdminOptionsRequest() : Promise.resolve({ data: [] }),
      ]);

      const adminOptions = (adminResponse.data || []).map((admin) => ({
        value: admin.id,
        label: `${admin.user?.email} (${admin.user?.first_name} ${admin.user?.last_name})`,
      }));

      return {
        courierOptions: courierResponse.data || [],
        adminOptions,
      };
    } catch (error) {
      console.error('Error fetching zone form options:', error);
      return rejectWithValue('Error al cargar información para los selectores');
    }
  },
  {
    condition: (_, { getState }) => !getState().zones.optionsLoaded,
  }
);

/**
 * Loads the zones of one admin as select options, for the forms that hang a
 * warehouse off a zone.
 *
 * Kept apart from `fetchZoneFormOptions` because it is scoped by owner: the
 * zones on offer are those of the admin the record belongs to, which is not
 * necessarily whoever is filling the form.
 *
 * @param {number|string} adminId
 * @returns {Promise<Array<{value: number, label: string}>>}
 */
export const fetchAdminZoneOptions = createAsyncThunk(
  'zones/fetchAdminZoneOptions',
  async (adminId, { rejectWithValue }) => {
    if (!adminId) return rejectWithValue('Admin ID no provisto');

    try {
      const response = await getAdminShippingZonesRequest(adminId);

      return (response.data?.serialized_shippingzones || []).map((zone) => ({
        value: zone.id,
        label: zone.name,
      }));
    } catch (error) {
      console.error('Error fetching admin shipping zones:', error);
      return rejectWithValue('No se pudieron cargar las zonas de despacho');
    }
  }
);

/**
 * Loads warehouses for the assignment modal.
 *
 * Requested on demand rather than alongside the rest of the options: the zones
 * list never needs them.
 */
export const fetchWarehouseOptions = createAsyncThunk(
  'zones/fetchWarehouseOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWarehouseOptionsRequest();

      return (response.data?.warehouses || []).map((warehouse) => ({
        value: warehouse.id,
        label: `${warehouse.name} (${warehouse?.company?.social_reason})`,
      }));
    } catch (error) {
      console.error('Error fetching warehouse options:', error);
      return rejectWithValue('Error al cargar las bodegas disponibles');
    }
  },
  {
    condition: (_, { getState }) => !getState().zones.warehousesLoaded,
  }
);
